import React, {useState, useEffect, useContext, useCallback} from 'react';
import axios from 'axios';
import { API_URL } from '../../constants';
import {Link, useNavigate, useParams} from 'react-router-dom';
import withAuth from "../../auth";
import { Breadcrumb } from 'react-bootstrap';
import moment from "moment";
import {AuthContext} from "../AuthContext";
import Spinner from "react-bootstrap/Spinner";
import {formatPhoneNumberWithMask} from "../../formatPhoneNumber";
import InputMask from "react-input-mask";
import WhitelabelBadge from "../WhitelabelBadge";
import DriverCargoProtectionDocument from "./DriverCargoProtectionDocument";
import DriverDocuments from './DriverDocuments';
import NPPlacesInput from "../google_map/NPPlacesInput";
import {GoogleMapsProvider} from "../google_map/GoogleMapsContext";
import {
    buildDriverUpdatePayload,
    buildEmployeeCreatePayload,
    getDriverProfileFormData,
} from "./driverPayloads";

const DriverProfile = () => {
    const { driverId } = useParams();
    const [driver, setDriver] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingSms, setLoadingSms] = useState(false);
    const navigate = useNavigate();
    const {token, setToken, hasPermission, hasRole} = useContext(AuthContext);

    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationBody, setNotificationBody] = useState('');
    const [isNotificationSent, setIsNotificationSent] = useState(false);
    const [showNotification, setShowNotification] = useState(false);

    const [smsMessage, setSmsMessage] = useState('');
    const [isSmsSent, setIsSmsSent] = useState(false);
    const [showSms, setShowSms] = useState(false);
    const [showBanking, setShowBanking] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isNewPasswordSubmitted, setIsNewPasswordSubmitted] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const [generatingLink, setGeneratingLink] = useState(false);
    const [loginHash, setLoginHash] = useState('');
    const [copyMessage, setCopyMessage] = useState('');
    const [vehicleListRefreshKey, setVehicleListRefreshKey] = useState(0);

    const refreshVehicleList = useCallback(() => {
        setVehicleListRefreshKey(current => current + 1);
    }, []);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_num: '',
        address: '',
        business_name: '',
        designation: '',
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [bankingInstitutions, setBankingInstitutions] = useState([]);
    const [bankingFormData, setBankingFormData] = useState({
        name_on_account: '',
        banking_institution_id: '',
        transit_num: '',
        institution_num: '',
        account_num: '',
    });
    const [isBankingSubmitted, setIsBankingSubmitted] = useState(false);

    const [uploadingFile, setUploadingFile] = useState(null);

    // Fetch the details of the driver
    useEffect(() => {
        axios.get(`${API_URL}/api/admin/drivers/${driverId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(response => {
                setDriver(response.data.data);
            })
            .catch(error => {
                console.error('There was an error fetching the driver details!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            });
    }, [driverId, navigate, setToken, token]);

    useEffect(() => {
        if (driver) {
            setFormData(getDriverProfileFormData(driver));
            setBankingFormData({
                name_on_account: driver.bank_account?.name_on_account || '',
                banking_institution_id: driver.bank_account?.banking_institution_id || '',
                transit_num: driver.bank_account?.transit_num || '',
                institution_num: driver.bank_account?.institution_num || '',
                account_num: driver.bank_account?.account_num || '',
            });
        }
    }, [driver]);

    useEffect(() => {
        axios.get(`${API_URL}/api/admin/banking-institutions`, {
            headers: {'Authorization': `Bearer ${token}`}
        })
            .then(response => {
                setBankingInstitutions(response.data.banks || []);
            })
            .catch(error => {
                console.error('There was an error fetching banking institutions!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            });
    }, [navigate, setToken, token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleBankingChange = (e) => {
        setBankingFormData({ ...bankingFormData, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        if (!window.confirm("Are you sure that you want to update the driver's data?")) {
            return;
        }

        setErrorMessage('');
        setIsSubmitted(true);
        try {
            const response = await axios.put(`${API_URL}/api/admin/drivers/${driverId}`, buildDriverUpdatePayload(formData), {
                headers: {'Authorization': `Bearer ${token}`}
            });
            alert(response.data.message);
            setDriver(response.data.driver);
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setErrorMessage(errorMessages);
            } else if (error?.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage('An error occurred. Please try again later.');
            }
        }
        finally {
            setIsSubmitted(false);
        }
    };

    const handleBankingSave = async () => {
        if (!window.confirm("Are you sure that you want to update the driver's banking information?")) {
            return;
        }

        setErrorMessage('');
        setIsBankingSubmitted(true);
        try {
            const response = await axios.put(`${API_URL}/api/admin/drivers/${driverId}/bank-account`, bankingFormData, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            alert(response.data.message);
            setDriver(response.data.driver);
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setErrorMessage(errorMessages);
            } else if (error?.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage('An error occurred. Please try again later.');
            }
        }
        finally {
            setIsBankingSubmitted(false);
        }
    };

    const handleApproveClick = () => {
        setLoading(true);
        axios.put(`${API_URL}/api/admin/drivers/${driverId}/approval-status`, {},{
            params: {
                status: 'approve',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error updating the approval status!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleResetApprovalClick = () => {
        setLoading(true);
        axios.put(`${API_URL}/api/admin/drivers/${driverId}/approval-status`, {},{
            params: {
                status: 'reset',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error updating the approval status!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleEnableSmsClick = () => {
        setLoadingSms(true);
        axios.put(`${API_URL}/api/admin/drivers/${driverId}/sms-status`, {},{
            params: {
                status: 'enable',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error enabling SMS notifications!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoadingSms(false);
            });
    };

    const handleDisableSmsClick = () => {
        setLoadingSms(true);
        axios.put(`${API_URL}/api/admin/drivers/${driverId}/sms-status`, {},{
            params: {
                status: 'disable',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error disabling SMS notifications!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoadingSms(false);
            });
    };

    const handleDeleteClick = () => {
        if (window.confirm("Are you sure that you want to delete this driver?")) {
            axios.delete(`${API_URL}/api/admin/drivers/${driverId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
                .then(response => {
                    alert('Driver deleted successfully!');
                    setDriver(null);
                    navigate('/drivers');
                })
                .catch(error => {
                    console.error('There was an error deleting the driver!', error);
                    if (error.response && error.response.status === 401) {
                        localStorage.removeItem('authToken');
                        setToken(null);
                        navigate('/');
                    }
                });
        }
    };

    const handleNewPasswordClick = async () => {
        if (!window.confirm("Could you please confirm if you would like to change the password for the driver?")) return false;

        setErrorMessage('');
        setIsNewPasswordSubmitted(true);
        try {
            const response = await axios.put(`${API_URL}/api/admin/drivers/${driverId}/password`, {password: newPassword}, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            alert(response.data.message);
            setNewPassword('');
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setErrorMessage(errorMessages);
            } else if (error?.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage('An error occurred. Please try again later.');
            }
        }
        finally {
            setIsNewPasswordSubmitted(false);
        }
    };

    const handleGenerateLoginHash = () => {
        if (!window.confirm("Generate temporary login hash for this driver?")) return;

        setGeneratingLink(true);
        setLoginHash('');

        axios.get(`${API_URL}/api/admin/drivers/temp-login/${driverId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(response => {
                const { hash } = response.data;
                setLoginHash(hash);
            })
            .catch(error => {
                alert(error.response?.data?.error || 'An error occurred.');
            })
            .finally(() => {
                setGeneratingLink(false);
            });
    };

    const handleCopyHash = () => {
        navigator.clipboard.writeText(loginHash).then(() => {
            setCopyMessage('Copied!');
            setTimeout(() => setCopyMessage(''), 2000);
        }).catch(() => {
            setCopyMessage('Failed to copy.');
            setTimeout(() => setCopyMessage(''), 2000);
        });
    };

    const handleSendPushNotification = () => {
        if (!window.confirm("Are you sure that you want to send the push notification to this driver?")) return false;

        setErrorMessage('');
        setIsNotificationSent(true);
        axios.post(`${API_URL}/api/admin/drivers/${driverId}/notify`, {title: notificationTitle, body: notificationBody}, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert('Push notification successfully sent!');
                setNotificationTitle('');
                setNotificationBody('');
            })
            .catch(error => {
                console.error('There was an error sending the push notification to the driver!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }

                if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                    setErrorMessage(errorMessages);
                } else if (error?.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setIsNotificationSent(false);
            });
    };

    const handleSendSms = () => {
        if (!window.confirm("Are you sure that you want to send the SMS to this driver?")) return false;

        setErrorMessage('');
        setIsSmsSent(true);
        axios.post(`${API_URL}/api/admin/drivers/${driverId}/send-sms`, {message: smsMessage}, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert('SMS successfully sent!');
                setSmsMessage('');
            })
            .catch(error => {
                console.error('There was an error sending the SMS to the driver!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }

                if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                    setErrorMessage(errorMessages);
                } else if (error?.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setIsSmsSent(false);
            });
    };

    // Function to render stars based on rating
    const renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const halfStar = rating - fullStars >= 0.5;
        const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

        return (
            <>
                {[...Array(fullStars)].map((e, i) =>
                    <i key={`full-${i}`} className="fa fa-star" style={{ color: '#FFD700' }}></i>
                )}
                {halfStar && <i className="fa fa-star-half-o" style={{ color: '#FFD700' }}></i>}
                {[...Array(emptyStars)].map((e, i) =>
                    <i key={`empty-${i}`} className="fa fa-star-o" style={{ color: '#FFD700' }}></i>
                )}
                &nbsp;({rating.toFixed(1)})
            </>
        );
    };
    const handleFileChange = async (file, documentType) => {
        if (!file) return;

        setErrorMessage('');
        setUploadingFile(documentType);

        try {
            const formData = new FormData();
            formData.append("image-upload", file);

            let apiEndpoint = "";
            switch (documentType) {
                case "front":
                    apiEndpoint = `/api/admin/drivers/${driverId}/upload-license-front`;
                    break;
                case "back":
                    apiEndpoint = `/api/admin/drivers/${driverId}/upload-license-back`;
                    break;
                case "proof":
                    apiEndpoint = `/api/admin/drivers/${driverId}/upload-proof-of-work`;
                    break;
                case "insurance":
                    apiEndpoint = `/api/admin/drivers/${driverId}/upload-insurance`;
                    break;
                default:
                    throw new Error("Invalid upload type");
            }

            await axios.post(
                `${API_URL}${apiEndpoint}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "Authorization": `Bearer ${token}`,
                    },
                },
            );

            alert("File uploaded successfully");
            window.location.reload();
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem("authToken");
                setToken(null);
                navigate("/");
            } else if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors)
                    .map((key) => errors[key].join(" "))
                    .join(" ");
                setErrorMessage(errorMessages);
            } else if (error?.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred. Please try again later.");
            }

            // Scroll to top when an error occurs
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
        finally {
            setUploadingFile(null);
        }
    };

    const togglePasswordVisibility = () => {
        setIsPasswordVisible((prev) => !prev);
    };

    const getDriverProfileTitle = () => {
        if (!driver) return 'Driver Profile';
        if (driver.type === 2) return 'Company Manager Driver Profile';
        if (driver.type === 3) return 'Company Employee Driver Profile';
        return 'Individual Driver Profile';
    };

    const driverProfileTitle = getDriverProfileTitle();
    const canUpdateBankingInfo = driver && [1, 2].includes(driver.type) && hasPermission('driver-update');

    return (
        <GoogleMapsProvider>
        <div className="container-fluid mt-3">
            <Breadcrumb>
                <Breadcrumb.Item href="/drivers">Drivers</Breadcrumb.Item>
                <Breadcrumb.Item active>{driverProfileTitle}</Breadcrumb.Item>
            </Breadcrumb>
            <h1 className="text-center">{driverProfileTitle} {driver ? driver.id : ''}</h1>
            {driver && (
                <div className="text-center mb-2">
                    <WhitelabelBadge whitelabelId={driver.whitelabel_id}/>
                </div>
            )}
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {driver ? (
                <div className="row">
                    <div className="col-xl-5">
                        <div className="card mb-3">
                            <div className="card-body" align={'center'}>
                                {driver.profile_photo &&
                                    <a href={driver.profile_photo} target="_blank" rel="noopener noreferrer">
                                        <img src={driver.profile_photo} width="100" alt={`${driver.name}'s Profile Photo`} className="mb-3" />
                                    </a>
                                }
                                {hasPermission('driver-update') ?
                                    <>
                                        {driver.company && (
                                            <>
                                                <div className="form-group row mb-2">
                                                    <label className="col-md-3 col-form-label">Business Name:</label>
                                                    <div className="col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="business_name"
                                                            value={formData.business_name}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group row mb-2">
                                                    <label className="col-md-3 col-form-label">Designation:</label>
                                                    <div className="col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="designation"
                                                            value={formData.designation}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <hr/>
                                            </>
                                        )}
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">Email:</label>
                                            <div className="col-md-9">
                                                <input type="text" className="form-control" name="email"
                                                       value={formData.email} onChange={handleChange}/>
                                            </div>
                                        </div>
                                        {driver.type === 3 &&
                                            <div className="form-group row mb-2">
                                                <label className="col-md-3 col-form-label">Username:</label>
                                                <div className="col-md-9">
                                                    <input type="text" className="form-control" name="username"
                                                           value={formData.username} onChange={handleChange}/>
                                                </div>
                                            </div>
                                        }
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">Phone:</label>
                                            <div className="col-md-9">

                                                <InputMask className='form-control' mask="+9 (999) 999-9999" name="phone_num" value={formData.phone_num}
                                                           onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label htmlFor="driver-address" className="col-md-3 col-form-label">Address:</label>
                                            <div className="col-md-9 text-start">
                                                <NPPlacesInput
                                                    inputId="driver-address"
                                                    inputName="address"
                                                    placeholder="Start typing the driver's address"
                                                    value={formData.address}
                                                    onAddressChange={(address) => setFormData((current) => ({...current, address}))}
                                                    onResultSelected={(address) => setFormData((current) => ({...current, address}))}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">First Name:</label>
                                            <div className="col-md-9">
                                                <input type="text" className="form-control" name="first_name"
                                                       value={formData.first_name} onChange={handleChange}/>
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">Last Name:</label>
                                            <div className="col-md-9">
                                                <input type="text" className="form-control" name="last_name"
                                                       value={formData.last_name} onChange={handleChange}/>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-center">
                                            <button className="btn btn-primary" onClick={handleSave}>
                                                {isSubmitted
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status"
                                                                 aria-hidden="true"/>&nbsp;Saving...</>
                                                    : "Save"
                                                }
                                            </button>
                                        </div>
                                        <hr/>
                                    </>
                                    : <>
                                        {driver.company && (
                                            <div className="mb-2">
                                                <h5 className="card-title">Company</h5>
                                                <p className="card-text">Business Name: <b>{driver.company.business_name}</b></p>
                                            </div>
                                        )}
                                        <p className="card-text">
                                            <b>{driver.name}</b><br/>
                                            <b>{driver.email}</b> <br/>
                                            <b>{formatPhoneNumberWithMask(driver.phone_num|| "",
                                                    "+9 999 999-9999")}</b><br/>
                                            Address: <b>{driver.address || 'Not provided'}</b>
                                        </p>
                                    </>
                                }
                                {hasPermission('driver-update') && (
                                    <>
                                        <div className="d-flex align-items-center justify-content-center">
                                            {loading ? (
                                                <div className="spinner-border mr-2" role="status">
                                                    <span className="sr-only">Loading...</span>
                                                </div>
                                            ) : (
                                                <div>
                                                    {driver.admin_approved ? (
                                                        <button className="btn btn-warning" style={{marginRight: '.5em'}} onClick={handleResetApprovalClick}>Reset Approval</button>
                                                    ) : (
                                                        <button className="btn btn-success" style={{marginRight: '.5em'}} onClick={handleApproveClick}>Approve</button>
                                                    )}
                                                </div>
                                            )}

                                            {loadingSms ? (
                                                <div className="spinner-border" role="status">
                                                    <span className="sr-only">Loading...</span>
                                                </div>
                                            ) : (
                                                <div>
                                                    {driver.sms_enabled ? (
                                                        <button
                                                            className="btn btn-warning"
                                                            style={{ marginRight: '.5em' }}
                                                            onClick={handleDisableSmsClick}
                                                        >
                                                            <i class="fa fa-comments"/>&nbsp; Disable SMS
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-success"
                                                            style={{ marginRight: '.5em' }}
                                                            onClick={handleEnableSmsClick}
                                                        >
                                                            <i className="fa fa-comments"/>&nbsp;Enable SMS
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <hr/>
                                        <div className="form-row align-items-center justify-content-center">
                                            <div className="col-auto">
                                                <div style={{position: "relative"}} className="mb-2">
                                                    <input className="form-control" name="password"
                                                           placeholder="New password"
                                                           type={isPasswordVisible ? "text" : "password"}
                                                           value={newPassword}
                                                           onChange={e => setNewPassword(e.target.value)}
                                                           autoComplete="new-password"
                                                           style={{paddingRight: "1.75rem"}}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={togglePasswordVisibility}
                                                        style={{
                                                            position: "absolute", right: 0, top: "50%",
                                                            transform: "translateY(-50%)",
                                                            padding: ".375rem .6rem",
                                                            background: "transparent", border: "none"
                                                        }}
                                                    >
                                                        <i className={`fa ${isPasswordVisible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="col-auto">
                                                <button className="btn btn-dark mb-2" onClick={handleNewPasswordClick}>
                                                    {isNewPasswordSubmitted
                                                        ? <><Spinner as="span" animation="border" size="sm"
                                                                     role="status"
                                                                     aria-hidden="true"/>&nbsp;Changing...</>
                                                        : "Change"
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                        {hasRole('super-admin') &&
                                        <>
                                            <hr/>
                                            <div className="text-center mt-3">
                                                <button className="btn btn-info" onClick={handleGenerateLoginHash} disabled={generatingLink}>
                                                    {generatingLink ? <Spinner as="span" animation="border" size="sm" /> : "Generate Temporary Login Hash"}
                                                </button>

                                                {loginHash && (
                                                    <div className="mt-2">
                                                        <div className="input-group justify-content-center align-items-center">
                                                            <input
                                                                type="text"
                                                                className="form-control text-center"
                                                                value={loginHash}
                                                                readOnly
                                                                style={{ maxWidth: '400px' }}
                                                            />
                                                            <button className={'btn' + (copyMessage ? ' btn-success' : ' btn-outline-secondary')} onClick={handleCopyHash}>
                                                                <i className="fa fa-copy"></i> &nbsp;
                                                                {copyMessage ?
                                                                    <span>{copyMessage}</span>
                                                                    : 'Copy'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </>}
                                        <hr/>
                                        <button className="btn btn-danger" onClick={handleDeleteClick}>Delete</button>
                                    </>
                                )}
                                <hr />
                                <p className="card-text">
                                    Average Rating: <b>{renderStars(driver.avg_rating || 0)}</b><br/>
                                    Onboarding State: <b>{driver.onboarding_state.name}</b>
                                    <br/>
                                    Heard About Us From: <b>{driver.how_did_you_hear_about_us?.referral_source}</b>
                                    <br/>
                                    Signup Type: <b>{driver.signup_type?.signup_type}</b>
                                </p>
                                <p className="card-text">
                                    Registered: <b>{moment(driver.created_at).format('YYYY-MM-DD HH:mm')}</b>
                                    <br/>
                                    Last Login: <b>{driver.last_login_at ? moment(driver.last_login_at).format('YYYY-MM-DD HH:mm') : 'unknown '}</b>
                                    <br/>
                                    IP: <b>{driver.last_login_ip || 'unknown'}</b>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-7">
            
                        {driver.company && <DriverList driverId={driverId} isManager={driver.type === 2} onWorkerVehicleChanged={refreshVehicleList} />}

                        <VehicleList driverId={driverId} refreshKey={vehicleListRefreshKey} />

                        {hasPermission('driver-update') && (
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5
                                        className="text-center"
                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                        onClick={() => setShowNotification(!showNotification)}
                                    >
                                        Send Push Notification {showNotification ? '▼' : '▶'}
                                    </h5>
                                    <div
                                        className={`fade-section ${showNotification ? 'show' : 'hide'}`}
                                    >
                                        <div className="form-group mb-2">
                                            <input className="form-control" type="text" placeholder="Title" value={notificationTitle}
                                                   onChange={(e) => setNotificationTitle(e.target.value)} />
                                        </div>
                                        <div className="form-group mb-2">
                                            <textarea className="form-control" style={{minHeight: '50px'}} placeholder="Body" value={notificationBody}
                                                      onChange={(e) => setNotificationBody(e.target.value)} />
                                        </div>
                                        <div align={'center'}>
                                            <button className="btn btn-primary"onClick={handleSendPushNotification}>
                                                {isNotificationSent
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Sending...</>
                                                    : 'Send'
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {hasPermission('driver-update') && (
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5
                                        className="text-center"
                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                        onClick={() => setShowSms(!showSms)}
                                    >
                                        Send SMS {showSms ? '▼' : '▶'}
                                    </h5>
                                    <div
                                        className={`fade-section ${showSms ? 'show' : 'hide'}`}
                                    >
                                        <div className="form-group mb-2">
                                            <textarea className="form-control" style={{minHeight: '50px'}} placeholder="Message" value={smsMessage}
                                                      onChange={(e) => setSmsMessage(e.target.value)} />
                                        </div>
                                        <div align={'center'}>
                                            <button className="btn btn-primary"onClick={handleSendSms}>
                                                {isSmsSent
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Sending...</>
                                                    : 'Send'
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {canUpdateBankingInfo && (
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5
                                        className="text-center"
                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                        onClick={() => setShowBanking(!showBanking)}
                                    >
                                        Banking Information {showBanking ? '▼' : '▶'}
                                    </h5>
                                    <div
                                        className={`fade-section ${showBanking ? 'show' : 'hide'}`}
                                    >
                                        <div className="form-group row mb-2">
                                            <label className="col-md-4 col-form-label">Name on Account:</label>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="name_on_account"
                                                    value={bankingFormData.name_on_account}
                                                    onChange={handleBankingChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-4 col-form-label">Bank:</label>
                                            <div className="col-md-8">
                                                <select
                                                    className="form-control"
                                                    name="banking_institution_id"
                                                    value={bankingFormData.banking_institution_id}
                                                    onChange={handleBankingChange}
                                                >
                                                    <option value="">Select bank</option>
                                                    {bankingInstitutions.map(institution => (
                                                        <option key={institution.id} value={institution.id}>
                                                            {institution.institution}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-4 col-form-label">Transit Number:</label>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength="5"
                                                    className="form-control"
                                                    name="transit_num"
                                                    value={bankingFormData.transit_num}
                                                    onChange={handleBankingChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-4 col-form-label">Institution Number:</label>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength="3"
                                                    className="form-control"
                                                    name="institution_num"
                                                    value={bankingFormData.institution_num}
                                                    onChange={handleBankingChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-4 col-form-label">Account Number:</label>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength="12"
                                                    className="form-control"
                                                    name="account_num"
                                                    value={bankingFormData.account_num}
                                                    onChange={handleBankingChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-center">
                                            <button className="btn btn-primary" onClick={handleBankingSave}>
                                                {isBankingSubmitted
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"/>&nbsp;Saving...</>
                                                    : "Save Banking Information"
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {hasPermission('driver-view') && (
                            <DriverDocuments
                                key={driverId}
                                canUpdate={hasPermission('driver-update')}
                                driverId={driverId}
                                documents={driver.driver_documents}
                                onUpload={handleFileChange}
                                uploadingFile={uploadingFile}
                            >
                                {({expanded}) => (
                                    <DriverCargoProtectionDocument
                                        driver={driver}
                                        enabled={expanded}
                                    />
                                )}
                            </DriverDocuments>
                        )}

                        {hasPermission('driver-update') && (
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5
                                        className="text-center"
                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                        onClick={() => setShowAgreement(!showAgreement)}
                                    >
                                        Agreement {showAgreement ? '▼' : '▶'}
                                    </h5>
                                    <div className={`fade-section ${showAgreement ? 'show' : 'hide'}`}>
                                        <p className="card-text">
                                            Type:&nbsp;
                                            <b>
                                                {driver.agreement_type === 1 ? <a href="https://navipickups.com/general-driver-contractor-agreement/" target="_blank" rel="noopener noreferrer">General Contractor's Agreement</a>
                                                    : driver.agreement_type === 2 ? <a href="https://navipickups.com/commercial-driver_contractor-agreement/" target="_blank" rel="noopener noreferrer">Commercial Contractor's Agreement</a>
                                                    : driver.agreement_type === 3 ? <a href="https://navipickups.com/cubecargo-vans-commercial-contractor-agreement/" target="_blank" rel="noopener noreferrer">Cube&Cargo Vans Commercial Contractor's Agreement</a>
                                                    : driver.agreement_type === 4 ? <a href="https://navipickups.com/commercial-driver_contractor-agreement-moffett/" target="_blank" rel="noopener noreferrer">Commercial Moffett Contractor's Agreement</a>
                                                    : ''}
                                            </b>
                                            <br/>
                                            Name: <b>{driver.agreement_name}</b><br/>
                                            Created At: <b>{driver.agreement_created_at ? moment(driver.agreement_created_at).format('YYYY-MM-DD HH:mm') : 'unknown '}</b>
                                            <br/>
                                            {driver.agreement_signature
                                                ?
                                                <a href={driver.agreement_signature} target="_blank" rel="noopener noreferrer">
                                                    <img src={driver.agreement_signature} alt="agreement_signature" style={{ width: "auto", height: "auto"}} />
                                                </a>
                                                : 'Not Uploaded'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="d-flex justify-content-center align-items-center" style={{height: '80vh'}}>
                    <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}

            <style jsx> {`
                .fade-section {
                  max-height: 0;
                  opacity: 0;
                  overflow: hidden;
                  transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
                }
                
                .fade-section.show {
                  max-height: 5000px;
                  opacity: 1;
                }
                
                .fade-section.hide {
                  max-height: 0;
                  opacity: 0;
                }            
            `}
            </style>
        </div>
        </GoogleMapsProvider>
    );
};

const VehicleList = ({driverId, refreshKey}) => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {token, setToken} = useContext(AuthContext);

    // Fetch vehicles of the driver
    useEffect(() => {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        axios.get(`${API_URL}/api/admin/drivers/${driverId}/vehicles`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(response => {
                setVehicles(response.data.data);
            })
            .catch(error => {
                console.error('There was an error fetching the vehicles!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [driverId, refreshKey, navigate, setToken]);

    return (
        <div className="card mb-3">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Vehicles</h5>
                    <Link to="/vehicles/create" state={{ driverId: driverId }} className="btn btn-sm btn-primary">
                        Add Vehicle
                    </Link>
                </div>
                {loading ? (
                    <div className="d-flex justify-content-center mb-2">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered text-center">
                            <thead>
                            <tr>
                                <th>ID / Make / Model / License</th>
                                <th>Truck Type</th>
                                <th>Drivers</th>
                            </tr>
                            </thead>
                            <tbody>
                            {vehicles.map(vehicle => (
                                <tr key={vehicle.id}>
                                    <td>
                                        <Link to={`/vehicles/${vehicle.id}`} target="_blank">
                                            <b>{'#'}{vehicle.id} {vehicle.make} {vehicle.model} {vehicle.plate_number}</b>
                                        </Link>
                                    </td>
                                    <td>{vehicle.truckType?.title}</td>
                                    <td>
                                        {vehicle.drivers && vehicle.drivers.length > 0 ? (
                                            vehicle.drivers
                                                .filter(driver => driver.type !== 2)
                                                .map(driver => (
                                                    <div key={driver.id}>
                                                        <Link to={`/drivers/${driver.id}`} target="_blank">
                                                            <b>{'#'}{driver.id} {driver.name}</b>
                                                        </Link>
                                                    </div>
                                                ))
                                        ) : (
                                            <span>No Driver</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const emptyWorkerForm = {
    first_name: '',
    last_name: '',
    username: '',
    password: '',
    email: '',
    phone_num: '',
    address: '',
    vehicle_id: '',
};

const DriverList = ({driverId, isManager, onWorkerVehicleChanged}) => {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [workerFormData, setWorkerFormData] = useState(emptyWorkerForm);
    const [createError, setCreateError] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [companyVehicles, setCompanyVehicles] = useState([]);
    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const [vehicleSearch, setVehicleSearch] = useState('');
    const [isWorkerVehicleDropdownOpen, setIsWorkerVehicleDropdownOpen] = useState(false);
    const [assignmentSearches, setAssignmentSearches] = useState({});
    const [openAssignmentDriverId, setOpenAssignmentDriverId] = useState(null);
    const [assigningDriverId, setAssigningDriverId] = useState(null);
    const [assignmentMessages, setAssignmentMessages] = useState({});
    const navigate = useNavigate();
    const {token, setToken, hasPermission} = useContext(AuthContext);

    const fetchDrivers = useCallback(() => {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        axios.get(`${API_URL}/api/admin/drivers/${driverId}/drivers`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(response => {
                setDrivers(response.data.data);
            })
            .catch(error => {
                console.error('There was an error fetching the drivers!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [driverId, navigate, setToken]);

    const fetchCompanyVehicles = useCallback(() => {
        setLoadingVehicles(true);
        const token = localStorage.getItem('authToken');
        axios.get(`${API_URL}/api/admin/drivers/${driverId}/vehicles`,
            {
                params: {
                    per_page: 250,
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(response => {
                setCompanyVehicles(response.data.data);
            })
            .catch(error => {
                console.error('There was an error fetching the company vehicles!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoadingVehicles(false);
            });
    }, [driverId, navigate, setToken]);

    // Fetch drivers of the driver
    useEffect(() => {
        fetchDrivers();
    }, [fetchDrivers]);

    const canManageWorkerVehicles = isManager && hasPermission('driver-update');

    useEffect(() => {
        if (canManageWorkerVehicles) {
            fetchCompanyVehicles();
        }
    }, [canManageWorkerVehicles, fetchCompanyVehicles]);

    const handleToggleCreateForm = () => {
        const isOpening = !showCreateForm;
        setShowCreateForm(isOpening);
        setCreateError('');

        if (!isOpening) {
            setWorkerFormData(emptyWorkerForm);
            setVehicleSearch('');
            setIsWorkerVehicleDropdownOpen(false);
        }
    };

    const handleWorkerChange = (event) => {
        const {name, value} = event.target;
        setWorkerFormData(current => ({...current, [name]: value}));
    };

    const getVehicleLabel = (vehicle) => `#${vehicle.id} ${vehicle.plate_number || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
    const getSelectableVehicles = (searchValue) => {
        const query = searchValue.trim().toLowerCase();
        return companyVehicles.filter(vehicle => getVehicleLabel(vehicle).toLowerCase().includes(query));
    };
    const getSelectedWorkerVehicle = () => companyVehicles.find(vehicle => String(vehicle.id) === String(workerFormData.vehicle_id));
    const getWorkerVehicleInputValue = () => isWorkerVehicleDropdownOpen
        ? vehicleSearch
        : (getSelectedWorkerVehicle() ? getVehicleLabel(getSelectedWorkerVehicle()) : 'No vehicle');
    const getAssignedVehicle = (workerDriver) => workerDriver.vehicles && workerDriver.vehicles.length > 0 ? workerDriver.vehicles[0] : null;
    const getAssignmentInputValue = (workerDriver) => openAssignmentDriverId === workerDriver.id
        ? (assignmentSearches[workerDriver.id] || '')
        : (getAssignedVehicle(workerDriver) ? getVehicleLabel(getAssignedVehicle(workerDriver)) : 'No vehicle');
    const getAssignmentSelectableVehicles = (workerDriver) => getSelectableVehicles(assignmentSearches[workerDriver.id] || '');
    const selectableVehicles = getSelectableVehicles(vehicleSearch);

    const handleWorkerVehicleSearchChange = (value) => {
        setIsWorkerVehicleDropdownOpen(true);
        setVehicleSearch(value);
    };

    const handleWorkerVehicleFocus = () => {
        setIsWorkerVehicleDropdownOpen(true);
        setVehicleSearch('');
    };

    const handleSelectWorkerVehicle = (vehicleId) => {
        setWorkerFormData(current => ({...current, vehicle_id: vehicleId ? String(vehicleId) : ''}));
        setVehicleSearch('');
        setIsWorkerVehicleDropdownOpen(false);
    };

    const handleCreateWorker = (event) => {
        event.preventDefault();
        setCreateError('');
        setIsCreating(true);

        axios.post(`${API_URL}/api/admin/drivers/${driverId}/drivers/create`, buildEmployeeCreatePayload(workerFormData),
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(response => {
                alert(response.data.message);
                setWorkerFormData(emptyWorkerForm);
                setVehicleSearch('');
                setIsWorkerVehicleDropdownOpen(false);
                setShowCreateForm(false);
                fetchDrivers();
                if (workerFormData.vehicle_id) onWorkerVehicleChanged();
            })
            .catch(error => {
                console.error('There was an error creating the worker driver!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                    return;
                }

                if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    setCreateError(Object.keys(errors).map(key => errors[key].join(' ')).join(' '));
                } else if (error?.response?.data?.message) {
                    setCreateError(error.response.data.message);
                } else {
                    setCreateError('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setIsCreating(false);
            });
    };

    const handleAssignmentSearchChange = (workerDriverId, value) => {
        setOpenAssignmentDriverId(workerDriverId);
        setAssignmentSearches(current => ({...current, [workerDriverId]: value}));
    };

    const handleAssignmentFocus = (workerDriver) => {
        setOpenAssignmentDriverId(workerDriver.id);
        setAssignmentSearches(current => ({...current, [workerDriver.id]: ''}));
    };

    const clearAssignmentMessageLater = (workerDriverId, text) => {
        setTimeout(() => {
            setAssignmentMessages(current => {
                if (!current[workerDriverId] || current[workerDriverId].text !== text) return current;
                const next = {...current};
                delete next[workerDriverId];
                return next;
            });
        }, 2500);
    };

    const handleUpdateWorkerVehicle = (workerDriver, vehicleId) => {
        const nextVehicleId = vehicleId ? String(vehicleId) : null;
        setAssigningDriverId(workerDriver.id);
        setOpenAssignmentDriverId(null);
        setAssignmentSearches(current => ({...current, [workerDriver.id]: ''}));
        setAssignmentMessages(current => ({...current, [workerDriver.id]: {type: 'muted', text: 'Saving...'}}));

        axios.put(`${API_URL}/api/admin/drivers/${driverId}/drivers/${workerDriver.id}/vehicle`, {vehicle_id: nextVehicleId},
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(() => {
                setAssignmentMessages(current => ({...current, [workerDriver.id]: {type: 'success', text: 'Saved'}}));
                clearAssignmentMessageLater(workerDriver.id, 'Saved');
                fetchDrivers();
                onWorkerVehicleChanged();
            })
            .catch(error => {
                console.error('There was an error updating the worker vehicle!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                    return;
                }

                let message = 'An error occurred. Please try again later.';
                if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    message = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                } else if (error?.response?.data?.message) {
                    message = error.response.data.message;
                }

                setAssignmentMessages(current => ({...current, [workerDriver.id]: {type: 'danger', text: message}}));
            })
            .finally(() => {
                setAssigningDriverId(null);
            });
    };

    return (
        <div className="card mb-3">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">{isManager ? 'Drivers' : 'Managers'}</h5>
                    {canManageWorkerVehicles && (
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={handleToggleCreateForm}
                        >
                            {showCreateForm ? 'Cancel' : 'Add Employee'}
                        </button>
                    )}
                </div>

                {showCreateForm && (
                    <form onSubmit={handleCreateWorker} className="border rounded p-2 mb-3">
                        {createError && <div className="alert alert-danger py-2">{createError}</div>}
                        <div className="form-group mb-2">
                            <label>First Name</label>
                            <input type="text" className="form-control form-control-sm" name="first_name" value={workerFormData.first_name} onChange={handleWorkerChange} required />
                        </div>
                        <div className="form-group mb-2">
                            <label>Last Name</label>
                            <input type="text" className="form-control form-control-sm" name="last_name" value={workerFormData.last_name} onChange={handleWorkerChange} required />
                        </div>
                        <div className="form-group mb-2">
                            <label>Username</label>
                            <input type="text" className="form-control form-control-sm" name="username" value={workerFormData.username} onChange={handleWorkerChange} required />
                        </div>
                        <div className="form-group mb-2">
                            <label>Password</label>
                            <input type="password" className="form-control form-control-sm" name="password" value={workerFormData.password} onChange={handleWorkerChange} required />
                        </div>
                        <div className="form-group mb-2">
                            <label>Email</label>
                            <input type="email" className="form-control form-control-sm" name="email" value={workerFormData.email} onChange={handleWorkerChange} />
                        </div>
                        <div className="form-group mb-2">
                            <label>Phone</label>
                            <InputMask className="form-control form-control-sm" mask="+9 (999) 999-9999" name="phone_num" value={workerFormData.phone_num} onChange={handleWorkerChange} />
                        </div>
                        <div className="form-group mb-2">
                            <label htmlFor="employee-address">Address (Optional)</label>
                            <NPPlacesInput
                                inputId="employee-address"
                                inputName="address"
                                inputClassName="form-control form-control-sm"
                                placeholder="Start typing the employee's address"
                                value={workerFormData.address}
                                onAddressChange={(address) => setWorkerFormData((current) => ({...current, address}))}
                                onResultSelected={(address) => setWorkerFormData((current) => ({...current, address}))}
                            />
                        </div>
                        <div className="form-group mb-2">
                            <label>Vehicle</label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search by ID, license plate, make, or model"
                                    value={getWorkerVehicleInputValue()}
                                    onFocus={handleWorkerVehicleFocus}
                                    onChange={(event) => handleWorkerVehicleSearchChange(event.target.value)}
                                    onBlur={() => setTimeout(() => setIsWorkerVehicleDropdownOpen(false), 150)}
                                    disabled={loadingVehicles}
                                />
                                {isWorkerVehicleDropdownOpen && (
                                    <div
                                        className="list-group position-absolute w-100 shadow-sm"
                                        style={{zIndex: 1000, maxHeight: '180px', overflowY: 'auto'}}
                                    >
                                        <button
                                            type="button"
                                            className="list-group-item list-group-item-action py-1"
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                handleSelectWorkerVehicle(null);
                                            }}
                                        >
                                            No vehicle
                                        </button>
                                        {selectableVehicles.length === 0 && companyVehicles.length > 0 && (
                                            <div className="list-group-item py-1 text-muted">No vehicles match search</div>
                                        )}
                                        {companyVehicles.length === 0 && !loadingVehicles && (
                                            <div className="list-group-item py-1 text-muted">No company vehicles found</div>
                                        )}
                                        {selectableVehicles.map(vehicle => (
                                            <button
                                                type="button"
                                                key={vehicle.id}
                                                className="list-group-item list-group-item-action py-1"
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    handleSelectWorkerVehicle(vehicle.id);
                                                }}
                                            >
                                                {getVehicleLabel(vehicle)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button type="submit" className="btn btn-sm btn-success" disabled={isCreating}>
                            {isCreating
                                ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Creating...</>
                                : 'Create Employee'
                            }
                        </button>
                    </form>
                )}

                {loading ? (
                    <div className="d-flex justify-content-center mb-2">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive" style={{overflow: canManageWorkerVehicles ? "visible" : undefined}}>
                        <table className="table table-sm table-bordered text-center">
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                {isManager && <>
                                    <th>Username</th>
                                    <th>Vehicles</th>
                                    {canManageWorkerVehicles && <th>Assign Vehicle</th>}
                                </>}
                            </tr>
                            </thead>
                            <tbody>
                            {drivers.map(driver => (
                                <tr key={driver.id}>
                                    <td>
                                        <Link to={`/drivers/${driver.id}`} target="_blank">
                                            <b>{driver.id}</b>
                                        </Link>
                                    </td>
                                    <td>
                                        <Link to={`/drivers/${driver.id}`} target="_blank">
                                            {driver.name}
                                        </Link>
                                    </td>
                                    {isManager && <>
                                        <td>{driver.username}</td>
                                        <td>
                                            {driver.vehicles && driver.vehicles.length > 0 ? (
                                                driver.vehicles.map((vehicle, index) => (
                                                    <div key={index}>
                                                        <Link to={`/vehicles/${vehicle.id}`} target="_blank">
                                                            <b>{'#'}{vehicle.id} {vehicle.make} {vehicle.model} {vehicle.plate_number}</b>
                                                        </Link>
                                                    </div>
                                                ))
                                            ) : (
                                                <span>No Truck</span>
                                            )}
                                        </td>
                                        {canManageWorkerVehicles && (
                                            <td style={{minWidth: '260px', position: 'relative', zIndex: openAssignmentDriverId === driver.id ? 1050 : 'auto'}}>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={getAssignmentInputValue(driver)}
                                                        onFocus={() => handleAssignmentFocus(driver)}
                                                        onChange={(event) => handleAssignmentSearchChange(driver.id, event.target.value)}
                                                        onBlur={() => setTimeout(() => setOpenAssignmentDriverId(current => current === driver.id ? null : current), 150)}
                                                        disabled={loadingVehicles || assigningDriverId === driver.id}
                                                        placeholder="Search vehicle"
                                                    />
                                                    {openAssignmentDriverId === driver.id && (
                                                        <div
                                                            className="list-group position-absolute w-100 shadow-sm"
                                                            style={{zIndex: 1000, maxHeight: '180px', overflowY: 'auto'}}
                                                        >
                                                            <button
                                                                type="button"
                                                                className="list-group-item list-group-item-action py-1"
                                                                onMouseDown={(event) => {
                                                                    event.preventDefault();
                                                                    handleUpdateWorkerVehicle(driver, null);
                                                                }}
                                                            >
                                                                No vehicle
                                                            </button>
                                                            {getAssignmentSelectableVehicles(driver).length === 0 && companyVehicles.length > 0 && (
                                                                <div className="list-group-item py-1 text-muted">No vehicles match search</div>
                                                            )}
                                                            {companyVehicles.length === 0 && !loadingVehicles && (
                                                                <div className="list-group-item py-1 text-muted">No company vehicles found</div>
                                                            )}
                                                            {getAssignmentSelectableVehicles(driver).map(vehicle => (
                                                                <button
                                                                    type="button"
                                                                    key={vehicle.id}
                                                                    className="list-group-item list-group-item-action py-1"
                                                                    onMouseDown={(event) => {
                                                                        event.preventDefault();
                                                                        handleUpdateWorkerVehicle(driver, vehicle.id);
                                                                    }}
                                                                >
                                                                    {getVehicleLabel(vehicle)}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {assignmentMessages[driver.id] && (
                                                    <small className={`text-${assignmentMessages[driver.id].type}`}>
                                                        {assignmentMessages[driver.id].text}
                                                    </small>
                                                )}
                                            </td>
                                        )}
                                    </>}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default withAuth(DriverProfile);
